import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { FormattedMessage } from "react-intl";

import logo from "../../../assets/img/variants/cleverfarm/login-logo.png";
import { LangSwitch } from "../../components/specific/LangSwitch/LangSwitch";
import Localization from "../../services/Localization.service";
import { getAppVariant } from "../../variants/getAppVariant";
import { type Variant } from "../../variants/variants";

const TAGLINE_KEYS: Partial<Record<Variant, string>> = {
  spzo: "common.metadata.spzo.description",
};

type Props = React.PropsWithChildren<{
  isFetching?: boolean;
}>;

const EntryContainer = ({
  children,
  isFetching,
}: Props): React.ReactElement => {
  const taglineId = TAGLINE_KEYS[getAppVariant()];

  return (
    <Container>
      <StyledPaper>
        <Logo alt="company logo" src={logo} />
        <StyledLangSwitch onLangChange={Localization.setLang} />
        {taglineId && (
          <Tagline data-testid="entry-tagline" variant="body2">
            <FormattedMessage id={taglineId} />
          </Tagline>
        )}
        <Content>{children}</Content>
        {isFetching && (
          <Spinner data-testid="spinner">
            <CircularProgress />
          </Spinner>
        )}
      </StyledPaper>
    </Container>
  );
};

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100vh",
  backgroundImage: `url(${theme.assets.login.background})`,
  backgroundPosition: "center center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  margin: 0,
  padding: 16,
  position: "relative",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: "rgba(255,255,255,0.95)",
  padding: "0px 24px 32px 24px",
  position: "relative",
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    width: 450,
  },
}));

const Content = styled("div")(({ theme }) => ({
  margin: theme.spacing(0, 4),
}));

const Logo = styled("img")(({ theme }) => ({
  ...theme.assets.login.style,
  content: `url(${theme.assets.login.src})`,
}));

const Tagline = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: theme.spacing(0, 4, 2),
  textAlign: "center",
}));

const StyledLangSwitch = styled(LangSwitch)({
  right: 8,
  position: "absolute",
  top: 8,
});

const Spinner = styled("div")({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  zIndex: 999,
  top: 0,
  left: 0,
  borderRadius: 2,
  backgroundColor: "rgba(236,236,236,0.9)",
});

export { EntryContainer };
